import { apiRequest, setToken } from '../../api/client'
import type {
  AuthTokenResponse,
  AuthUser,
  GameStatePayload,
  Language,
  Subscription,
} from '../../types/auth'
import type { AppRepository } from './types'

export const apiRepository: AppRepository = {
  auth: {
    register: async input => {
      const data = await apiRequest<AuthTokenResponse>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(input),
      })
      setToken(data.token)
      return data
    },
    login: async input => {
      const data = await apiRequest<AuthTokenResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      })
      setToken(data.token)
      return data
    },
    me: () => apiRequest<AuthUser>('/api/auth/me'),
    updateLanguage: (language: Language) =>
      apiRequest<AuthUser>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ language }),
      }),
    logout: async () => {
      try {
        await apiRequest('/api/auth/logout', { method: 'POST' })
      } finally {
        setToken(null)
      }
    },
  },

  subscriptions: {
    getMine: () => apiRequest<Subscription>('/api/subscriptions/me'),
    createCheckout: interval =>
      apiRequest<{ url: string }>('/api/subscriptions/checkout', {
        method: 'POST',
        body: JSON.stringify({ interval }),
      }),
    createPortal: () =>
      apiRequest<{ url: string }>('/api/subscriptions/portal', {
        method: 'POST',
      }),
  },

  gameState: {
    get: async () => {
      try {
        const data = await apiRequest<{ success: boolean; gameState: GameStatePayload }>(
          '/api/game-state'
        )
        return data.gameState ?? null
      } catch (e) {
        if ((e as { status?: number }).status === 401) return null
        throw e
      }
    },
    put: async state => {
      const data = await apiRequest<{ success: boolean; gameState: GameStatePayload }>(
        '/api/game-state',
        {
          method: 'PUT',
          body: JSON.stringify(state),
        }
      )
      return data.gameState ?? null
    },
    getRank: async (limit = 20) => {
      const data = await apiRequest<{
        success: boolean
        ranks: Array<{
          rank: number
          userId: string
          email: string
          totalScore: number
          highestNumber: number
        }>
      }>(`/api/game-state/rank?limit=${limit}`)
      return data.ranks ?? []
    },
  },
}
